/**
 * Phase 8 T-13 §13 D — Team members panel (Wrangle `240.png`).
 *
 * Invite bar at the top: email input + role dropdown + Add button.
 * Members table below: avatar · name · email · role · last seen · ⋮ menu.
 * Hairline row separators, no cards.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { MoreHorizontal, UserPlus } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { orgService } from '@core/supabase/services/orgService';
import { PrivateStorageAvatar } from '@shared/components/ui/PrivateStorageAvatar';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { Users } from 'lucide-react';
import type { OrgRole, OrgUser } from '@core/types';

const ROLE_ORDER: OrgRole[] = [
  'owner', 'admin', 'senior_recruiter', 'junior_recruiter', 'viewer',
];
const ROLE_LABEL: Record<OrgRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  senior_recruiter: 'Senior Recruiter',
  junior_recruiter: 'Junior Recruiter',
  viewer: 'Viewer',
};
const ROLE_COLOR: Record<OrgRole, string> = {
  owner:            '#f59e0b',
  admin:            '#8B80F9',
  senior_recruiter: '#22c55e',
  junior_recruiter: '#3b82f6',
  viewer:           '#94a3b8',
};

function initials(name: string | null | undefined, email: string): string {
  const src = name ?? email;
  const parts = src.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'U';
  return parts.length === 1
    ? parts[0][0].toUpperCase()
    : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatLastSeen(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diff = now - d.getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 2) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

interface MemberRowMenuProps {
  member: OrgUser;
  isYou: boolean;
  canManage: boolean;
  onRemove: () => void;
}

function MemberRowMenu({ member, isYou, canManage, onRemove }: MemberRowMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!canManage || isYou || member.role === 'owner') return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Member actions"
        className="p-1 rounded-[6px] hover:vscode-bg-hover vscode-text-tertiary hover:vscode-text-primary transition-colors"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-7 z-30 min-w-[140px] rounded-[10px] border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)] shadow-md py-1 text-[13px]">
          <button
            type="button"
            onClick={() => { setOpen(false); onRemove(); }}
            className="w-full text-left px-3 py-2 text-red-500 hover:bg-red-50/60 dark:hover:bg-red-950/30 transition-colors"
          >
            Remove member
          </button>
        </div>
      )}
    </div>
  );
}

export function SettingsTeamPage() {
  const { user } = useAuth();
  const perms = usePermissions();

  const [members, setMembers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(true);

  /* invite bar state */
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgRole>('junior_recruiter');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  /* remove confirm */
  const [removing, setRemoving] = useState<OrgUser | null>(null);

  const load = useCallback(() => {
    if (!user?.orgId) return;
    orgService.getTeamMembers(user.orgId).then(setMembers).catch(() => setMembers([])).finally(() => setLoading(false));
  }, [user?.orgId]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async () => {
    if (!user?.orgId || !inviteEmail.trim()) return;
    setInviting(true);
    setInviteError(null);
    try {
      const newM = await orgService.addMember(user.orgId, {
        full_name: inviteName.trim() || inviteEmail.trim().split('@')[0],
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setMembers((prev) => [...prev, newM]);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('junior_recruiter');
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Could not add member');
    } finally {
      setInviting(false);
    }
  };

  const handleRemove = async (m: OrgUser) => {
    if (!user?.orgId) return;
    await orgService.deactivateMember(user.orgId, m.keycloak_sub);
    setMembers((prev) => prev.filter((x) => x.id !== m.id));
    setRemoving(null);
  };

  return (
    <div>
      {/* Section header */}
      <h2 className="text-[15px] font-semibold mb-1">Team</h2>
      <p className="text-[12px] vscode-text-tertiary mb-5">
        Manage who has access to your organisation workspace.
      </p>

      {/* ── Invite bar ── */}
      {perms.canManageTeam && (
        <div className="mb-6 rounded-[10px] border border-[var(--vscode-border-primary)] bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_50%,transparent)] p-4">
          <p className="text-[12px] font-semibold vscode-text-secondary mb-3 uppercase tracking-wide">
            Invite member
          </p>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-[11px] vscode-text-tertiary mb-1 block">Work email</label>
              <input
                className="input w-full h-8 text-[13px]"
                type="email"
                placeholder="name@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void handleInvite()}
              />
            </div>
            <div className="w-[160px]">
              <label className="text-[11px] vscode-text-tertiary mb-1 block">Role</label>
              <select
                className="input w-full h-8 text-[13px]"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as OrgRole)}
              >
                {ROLE_ORDER.filter((r) => r !== 'owner').map((r) => (
                  <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              disabled={inviting || !inviteEmail.trim()}
              onClick={() => void handleInvite()}
              className="btn-primary h-8 px-4 text-[13px] flex items-center gap-1.5 self-end"
            >
              <UserPlus className="w-3.5 h-3.5" />
              {inviting ? 'Adding…' : 'Add'}
            </button>
          </div>
          {inviteError && (
            <p className="mt-2 text-[12px] text-red-500">{inviteError}</p>
          )}
        </div>
      )}

      {/* ── Members table ── */}
      {loading ? (
        <FullPageSpinner />
      ) : members.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No team members yet"
          description="Add users via the invite bar above."
        />
      ) : (
        <div className="border border-[var(--vscode-border-primary)] rounded-[10px] overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_1fr_140px_100px_36px] gap-3 px-4 py-2.5 border-b border-[var(--vscode-border-primary)] bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_40%,transparent)]">
            <span className="text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary">Member</span>
            <span className="text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary">Email</span>
            <span className="text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary">Role</span>
            <span className="text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary">Last seen</span>
            <span />
          </div>

          {/* Rows */}
          {members.map((m, idx) => {
            const isYou = m.keycloak_sub === user?.id || m.email === user?.email;
            const color = ROLE_COLOR[m.role] ?? '#94a3b8';
            const ini = initials(m.display_name, m.email);
            return (
              <div
                key={m.id}
                className={[
                  'grid grid-cols-[1fr_1fr_140px_100px_36px] gap-3 px-4 py-3 items-center text-[13px]',
                  idx < members.length - 1
                    ? 'border-b border-[var(--vscode-border-primary)]'
                    : '',
                  'hover:bg-[color-mix(in_srgb,var(--vscode-bg-hover)_60%,transparent)] transition-colors',
                ].join(' ')}
              >
                {/* Avatar + name */}
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold overflow-hidden"
                    style={{ background: `color-mix(in srgb, ${color} 18%, var(--vscode-bg-tertiary))`, color }}
                  >
                    {m.avatar_url ? (
                      <PrivateStorageAvatar
                        path={m.avatar_url}
                        className="w-full h-full object-cover"
                        fallback={<span>{ini}</span>}
                      />
                    ) : ini}
                  </div>
                  <span className="truncate font-medium vscode-text-primary">
                    {m.display_name ?? m.email.split('@')[0]}
                    {isYou && <span className="font-normal vscode-text-tertiary"> · you</span>}
                  </span>
                </div>

                {/* Email */}
                <span className="truncate vscode-text-secondary">{m.email}</span>

                {/* Role chip */}
                <span
                  className="inline-flex items-center w-fit rounded-full px-2 py-0.5 text-[11px] font-medium"
                  style={{
                    background: `color-mix(in srgb, ${color} 14%, var(--vscode-bg-secondary))`,
                    color,
                  }}
                >
                  {ROLE_LABEL[m.role]}
                </span>

                {/* Last seen */}
                <span className="vscode-text-tertiary text-[12px]">
                  {formatLastSeen((m as OrgUser & { last_seen_at?: string }).last_seen_at)}
                </span>

                {/* Menu */}
                <MemberRowMenu
                  member={m}
                  isYou={isYou}
                  canManage={perms.canManageTeam}
                  onRemove={() => setRemoving(m)}
                />
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && void handleRemove(removing)}
        title="Remove team member"
        subtitle="Immediate loss of organisation access"
        message={`Remove ${removing?.display_name ?? removing?.email ?? 'this member'}? They will lose access immediately.`}
        confirmLabel="Remove"
        requireTypedConfirm={removing?.display_name ?? removing?.email ?? 'DELETE'}
      />
    </div>
  );
}
